import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type {
  RecipeOwnerKind,
  RecipeRowInput,
  RecipeWeights,
  StockBalanceRow,
  StockBalancesResponse,
  StockCostsResponse,
  StockDocument,
  StockDocumentLineInput,
  StockIngredient,
  StockIngredientsResponse,
  StockMovement,
  StockSupplier,
  WriteOffReason
} from "../../api/types";
import { useAppState } from "../../state/appState";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty, Modal } from "../../ui/components";
import { IngredientPicker, type PickerItem } from "../../ui/IngredientPicker";
import { dateTime, money } from "../../ui/format";
import { formatQuantity, UNIT_TITLES } from "../../ui/units";
import { InventorySheet } from "./InventorySheet";
import { ShelfLifePanel } from "./ShelfLife";
import { StockReportsPanel } from "./StockReports";

type Tab = "balances" | "recipes" | "documents" | "shelf" | "inventory" | "reports" | "catalog" | "suppliers";

const TABS: { id: Tab; title: string }[] = [
  { id: "balances", title: "Остатки" },
  { id: "recipes", title: "Техкарты" },
  { id: "documents", title: "Документы" },
  { id: "shelf", title: "Сроки годности" },
  { id: "inventory", title: "Инвентаризация" },
  { id: "reports", title: "Отчёты" },
  { id: "catalog", title: "Номенклатура" },
  { id: "suppliers", title: "Поставщики" }
];

const WRITE_OFF_REASONS: { id: WriteOffReason; title: string }[] = [
  { id: "spoilage", title: "Порча" },
  { id: "waste", title: "Брак и проливы" },
  { id: "staff_meal", title: "Питание персонала" },
  { id: "tasting", title: "Дегустация" },
  { id: "other", title: "Другое" }
];

const DOCUMENT_TITLES: Record<StockDocument["type"], string> = {
  supply: "Приход",
  writeoff: "Списание",
  supplier_return: "Возврат поставщику",
  production: "Приготовление",
  processing: "Переработка",
  inventory: "Инвентаризация"
};

/** Пустая строка документа: номенклатура выбирается, единица подставляется за ней. */
function emptyLine(): StockDocumentLineInput {
  return { ingredientId: "", quantity: 0, unit: "", price: 0, priceUnit: "" };
}

/**
 * Сколько штук в привезённом весе.
 *
 * Сосиски считают штуками, а поставщик привозит килограммами: «десять кило»
 * надо превратить в тысячу сосисок по десять граммов. Делить в уме на складе,
 * где ещё пятнадцать строк накладной, — это ошибка, которую заметят только
 * на инвентаризации.
 *
 * Дробная штука округляется вниз: половины сосиски на складе не бывает, а
 * лишние граммы — это допуск весов, а не товар.
 */
function piecesFromWeight(weight: number, unit: string, pieceWeightG: number | null): number {
  if (!pieceWeightG || pieceWeightG <= 0 || !(weight > 0)) return 0;

  const grams = unit === "kg" ? weight * 1000 : weight;
  return Math.floor(grams / pieceWeightG);
}

/**
 * Сколько базовых единиц в одной единице цены.
 *
 * Нужно, чтобы посчитать сумму строки: когда цену назвали за килограмм, а
 * количество ввели в штуках, перемножать их напрямую нельзя. Пересчёт на
 * сервере остаётся главным — здесь он только показывает оператору итог,
 * который тот увидит в проведённом документе.
 */
function priceFactor(ingredient: StockIngredient | undefined, priceUnit: string): number {
  if (!ingredient) return 1;
  if (ingredient.unit === "pcs" && priceUnit === "kg") {
    return ingredient.pieceWeightG ? 1000 / ingredient.pieceWeightG : 1;
  }
  return priceUnit === "kg" || priceUnit === "l" ? 1000 : 1;
}

/** Сумма строки: цена за базовую единицу, умноженная на количество в базовых. */
function lineAmount(line: StockDocumentLineInput, ingredient: StockIngredient | undefined): number {
  const priceUnit = line.priceUnit || line.unit;
  const entryFactor = line.unit === "kg" || line.unit === "l" ? 1000 : 1;
  const baseQuantity = (line.quantity || 0) * entryFactor;

  return ((line.price || 0) / priceFactor(ingredient, priceUnit)) * baseQuantity;
}

export function StockPage() {
  const { can } = useSession();
  const { run, notify } = useToasts();

  const [tab, setTab] = useState<Tab>("balances");
  const [balances, setBalances] = useState<StockBalancesResponse | null>(null);
  const [catalog, setCatalog] = useState<StockIngredientsResponse | null>(null);
  const [suppliers, setSuppliers] = useState<StockSupplier[]>([]);
  const [documents, setDocuments] = useState<StockDocument[]>([]);
  const [movements, setMovements] = useState<{ ingredient: StockIngredient; items: StockMovement[] } | null>(null);
  const [loading, setLoading] = useState(true);

  const [costs, setCosts] = useState<StockCostsResponse | null>(null);
  const { snapshot } = useAppState();
  const [supplyOpen, setSupplyOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [processingOpen, setProcessingOpen] = useState(false);
  const [ingredientOpen, setIngredientOpen] = useState(false);
  const [ingredientEdit, setIngredientEdit] = useState<StockIngredient | null>(null);
  const [openDocument, setOpenDocument] = useState<StockDocument | null>(null);
  const [editSupply, setEditSupply] = useState<StockDocument | null>(null);
  /**
   * Что показывать в остатках.
   *
   * Когда номенклатуры сотня, «11 заканчивается» — бесполезная цифра: чтобы
   * узнать, что именно, приходится глазами искать красные строки. Показатель
   * и есть вход в этот список.
   */
  const [balanceFilter, setBalanceFilter] = useState<"all" | "low" | "negative">("all");
  const [supplierDocuments, setSupplierDocuments] = useState<StockSupplier | null>(null);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [productionFor, setProductionFor] = useState<StockIngredient | null>(null);
  const [recipeFor, setRecipeFor] = useState<{
    owner: RecipeOwnerKind;
    id: string;
    title: string;
    subtitle: string;
  } | null>(null);

  const manage = can("stock.manage");

  /**
   * Проводит ли эта установка складские документы сама.
   *
   * Проведение меняет остаток и себестоимость, а скользящая средняя зависит от
   * порядка, в котором прошли приход и расход. Поэтому проводит только та
   * сторона, где физически лежит товар, — точка. В облаке остаётся приходная
   * накладная: её осмысленно занести по фотографии, а провести её точка
   * возьмётся сама, как только увидит.
   *
   * Сервер это правило проверяет и без интерфейса. Кнопки убраны затем, чтобы
   * владелец не заполнял форму, которую всё равно отклонят.
   */
  const postsHere = snapshot?.settings.appRole !== "cloud";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [balanceData, catalogData, supplierData, documentData, costData] = await Promise.all([
        api.get<StockBalancesResponse>("/api/admin/stock/balances"),
        api.get<StockIngredientsResponse>("/api/admin/stock/ingredients"),
        api.get<{ items: StockSupplier[] }>("/api/admin/stock/suppliers"),
        api.get<{ items: StockDocument[] }>("/api/admin/stock/documents"),
        api.get<StockCostsResponse>("/api/admin/stock/costs")
      ]);

      setBalances(balanceData);
      setCatalog(catalogData);
      setSuppliers(supplierData.items);
      setDocuments(documentData.items);
      setCosts(costData);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить склад", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openMovements(ingredient: StockIngredient) {
    try {
      const data = await api.get<{ items: StockMovement[] }>(
        `/api/admin/stock/movements?ingredientId=${ingredient.id}`
      );
      setMovements({ ingredient, items: data.items });
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить движение", "error");
    }
  }

  async function cancelDocument(document: StockDocument) {
    const confirmed = confirm(
      `Отменить ${DOCUMENT_TITLES[document.type].toLowerCase()} №${document.number}?\n\n` +
        (document.status === "pending"
          ? "Документ ещё не проведён — остатки он не менял и не изменит. Он останется в журнале отменённым."
          : "Остатки вернутся к состоянию до документа. Сам документ и его отмена останутся в журнале.")
    );
    if (!confirmed) return;

    const ok = await run(
      () => api.once.post(`/api/admin/stock/documents/${document.id}/cancel`),
      `Документ №${document.number} отменён`
    );
    if (ok) await load();
  }

  if (loading && !balances) return <Empty>Загрузка склада…</Empty>;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Склад</p>
          <h1>Остатки и документы</h1>
        </div>
        {manage ? (
          <div className="row-actions">
            {postsHere ? (
              <>
                <button type="button" className="btn btn-sm" onClick={() => setProcessingOpen(true)}>
                  Переработка
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setReturnOpen(true)}>
                  Возврат
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setWriteOffOpen(true)}>
                  Списание
                </button>
              </>
            ) : null}
            <button type="button" className="btn btn-sm btn-primary" onClick={() => setSupplyOpen(true)}>
              + Приход
            </button>
          </div>
        ) : null}
      </div>

      {manage && !postsHere ? (
        <p className="hint">
          Накладную можно занести отсюда — точка проведёт её у себя, как только выйдет на связь.
          Списание, возврат, переработку и инвентаризацию делают на точке: они меняют остаток
          и себестоимость, а считать это должна одна сторона — та, где лежит товар.
        </p>
      ) : null}

      {balances ? (
        <section className="metrics">
          <div className="metric">
            <b>{money(balances.totals.value)}</b>
            <span>стоимость запаса</span>
          </div>
          <button
            type="button"
            className={`metric metric-link ${balanceFilter === "low" ? "" : ""}`}
            aria-pressed={balanceFilter === "low"}
            onClick={() => {
              setTab("balances");
              setBalanceFilter(current => (current === "low" ? "all" : "low"));
            }}
          >
            <b>{balances.totals.lowCount}</b>
            <span>заканчивается</span>
          </button>
          <button
            type="button"
            className={`metric metric-link ${balances.totals.negativeCount > 0 ? "metric-alert" : ""}`}
            aria-pressed={balanceFilter === "negative"}
            onClick={() => {
              setTab("balances");
              setBalanceFilter(current => (current === "negative" ? "all" : "negative"));
            }}
          >
            <b>{balances.totals.negativeCount}</b>
            <span>ушло в минус</span>
          </button>
        </section>
      ) : null}

      <nav className="tabs" aria-label="Разделы склада">
        {TABS.map(item => (
          <button
            key={item.id}
            type="button"
            className={`tab ${tab === item.id ? "active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.title}
          </button>
        ))}
      </nav>

      {tab === "balances" ? (
        <BalancesTable
          balances={balances}
          filter={balanceFilter}
          onFilter={setBalanceFilter}
          onMovements={openMovements}
        />
      ) : null}

      {tab === "recipes" ? (
        <RecipesTable
          costs={costs}
          manage={manage}
          onEdit={(owner, id, title, subtitle) => setRecipeFor({ owner, id, title, subtitle })}
        />
      ) : null}

      {tab === "documents" ? (
        <>
          <PeriodPanel />
          <DocumentsTable
            documents={documents}
            manage={manage}
            onCancel={cancelDocument}
            onEdit={setEditSupply}
            onOpen={setOpenDocument}
          />
        </>
      ) : null}

      {/* Ведомость пересобирается при каждом открытии вкладки: расчётный
          остаток устаревает с каждым оплаченным заказом. */}
      {tab === "inventory" ? <InventorySheet manage={manage} onSaved={load} /> : null}

      {tab === "shelf" ? <ShelfLifePanel manage={manage} onChanged={load} /> : null}

      {tab === "reports" ? <StockReportsPanel /> : null}

      {tab === "catalog" ? (
        <CatalogTable
          catalog={catalog}
          manage={manage}
          onCreate={() => setIngredientOpen(true)}
          onEdit={setIngredientEdit}
          onEditRecipe={ingredient =>
            setRecipeFor({
              owner: "prep",
              id: ingredient.id,
              title: ingredient.title,
              subtitle: "закладка заготовки"
            })
          }
          onProduce={setProductionFor}
          onReload={load}
        />
      ) : null}

      {tab === "suppliers" ? (
        <SuppliersTable
          suppliers={suppliers}
          documents={documents}
          manage={manage}
          onCreate={() => setSupplierOpen(true)}
          onOpen={setSupplierDocuments}
        />
      ) : null}

      {supplyOpen && catalog ? (
        <DocumentModal
          title="Приходная накладная"
          ingredients={catalog.items}
          suppliers={suppliers}
          kind="supply"
          onClose={() => setSupplyOpen(false)}
          onSaved={async () => {
            setSupplyOpen(false);
            await load();
          }}
        />
      ) : null}

      {writeOffOpen && catalog ? (
        <DocumentModal
          title="Списание"
          ingredients={catalog.items}
          suppliers={[]}
          kind="writeoff"
          onClose={() => setWriteOffOpen(false)}
          onSaved={async () => {
            setWriteOffOpen(false);
            await load();
          }}
        />
      ) : null}

      {returnOpen && catalog ? (
        <DocumentModal
          title="Возврат поставщику"
          ingredients={catalog.items}
          suppliers={suppliers}
          kind="return"
          onClose={() => setReturnOpen(false)}
          onSaved={async () => {
            setReturnOpen(false);
            await load();
          }}
        />
      ) : null}

      {processingOpen && catalog ? (
        <ProcessingModal
          ingredients={catalog.items}
          onClose={() => setProcessingOpen(false)}
          onSaved={async () => {
            setProcessingOpen(false);
            await load();
          }}
        />
      ) : null}

      {openDocument ? (
        <DocumentCard document={openDocument} onClose={() => setOpenDocument(null)} />
      ) : null}

      {editSupply && catalog ? (
        <DocumentModal
          title={`Приход №${editSupply.number} · исправление`}
          ingredients={catalog.items}
          suppliers={suppliers}
          kind="supply"
          edit={editSupply}
          onClose={() => setEditSupply(null)}
          onSaved={async () => {
            setEditSupply(null);
            await load();
          }}
        />
      ) : null}

      {supplierDocuments ? (
        <SupplierDocumentsModal
          supplier={supplierDocuments}
          documents={documents.filter(item => item.supplierId === supplierDocuments.id)}
          onClose={() => setSupplierDocuments(null)}
          onOpen={document => {
            setSupplierDocuments(null);
            setOpenDocument(document);
          }}
        />
      ) : null}

      {(ingredientOpen || ingredientEdit) && catalog ? (
        <IngredientModal
          ingredient={ingredientEdit}
          groups={catalog.groups}
          onClose={() => {
            setIngredientOpen(false);
            setIngredientEdit(null);
          }}
          onSaved={async () => {
            setIngredientOpen(false);
            setIngredientEdit(null);
            await load();
          }}
        />
      ) : null}

      {supplierOpen ? (
        <SupplierModal
          onClose={() => setSupplierOpen(false)}
          onSaved={async () => {
            setSupplierOpen(false);
            await load();
          }}
        />
      ) : null}

      {recipeFor && catalog ? (
        <RecipeModal
          owner={recipeFor.owner}
          ownerId={recipeFor.id}
          title={recipeFor.title}
          subtitle={recipeFor.subtitle}
          ingredients={catalog.items}
          manage={manage}
          onClose={() => setRecipeFor(null)}
          onSaved={load}
        />
      ) : null}

      {productionFor ? (
        <ProductionModal
          prep={productionFor}
          onClose={() => setProductionFor(null)}
          onSaved={async () => {
            setProductionFor(null);
            await load();
          }}
        />
      ) : null}

      {movements ? (
        <Modal title={`Движение: ${movements.ingredient.title}`} onClose={() => setMovements(null)}>
          <MovementsTable ingredient={movements.ingredient} items={movements.items} />
        </Modal>
      ) : null}
    </>
  );
}

function RecipesTable({
  costs,
  manage,
  onEdit
}: {
  costs: StockCostsResponse | null;
  manage: boolean;
  onEdit: (owner: RecipeOwnerKind, id: string, title: string, subtitle: string) => void;
}) {
  if (!costs) return null;

  const withRecipe = costs.dishes.filter(dish => dish.hasRecipe).length;

  return (
    <>
      <section className="panel">
        <header>
          <div>
            <h2>Блюда</h2>
            <p>
              Карт заполнено: {withRecipe} из {costs.dishes.length}. Блюдо без карты не списывает сырьё и не участвует
              в автостопе.
            </p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Блюдо</th>
                <th className="num">Цена</th>
                <th className="num">Себестоимость</th>
                <th className="num">Наценка</th>
                <th className="num">Фудкост</th>
                <th>Карта</th>
              </tr>
            </thead>
            <tbody>
              {costs.dishes.map(dish => (
                <tr key={dish.productId}>
                  <td>
                    <strong>{dish.title}</strong>
                    {dish.unpricedCount > 0 ? (
                      <>
                        <br />
                        <small className="muted">
                          без цены позиций: {dish.unpricedCount} — не было прихода
                        </small>
                      </>
                    ) : null}
                  </td>
                  <td className="num">{money(dish.price)}</td>
                  <td className="num">{dish.hasRecipe ? money(dish.cost) : "—"}</td>
                  <td className="num">{dish.margin === null ? "—" : money(dish.margin)}</td>
                  <td className="num">
                    {dish.foodCost === null ? (
                      <span className="muted">—</span>
                    ) : (
                      <FoodCostBadge value={dish.foodCost} />
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => onEdit("product", dish.productId, dish.title, "техкарта блюда")}
                      >
                        {manage ? (dish.hasRecipe ? "Изменить" : "Заполнить") : "Показать"}
                      </button>
                      {dish.hasRecipe ? (
                        /*
                         * Карта открывается вкладкой, а не скачивается: её печатают
                         * и подписывают, и путь «скачать — найти — открыть» тут
                         * лишний. Форма ОП-1 утверждена вместе с расположением
                         * граф, поэтому это страница, а не выгрузка.
                         */
                        <a
                          className="btn btn-sm"
                          href={`/api/admin/stock/costing-card/${dish.productId}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Калькуляция
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Модификаторы</h2>
            <p>
              Добавка списывает своё сырьё сверх карты блюда. Исключение убирает ровно столько, сколько положила бы
              карта, — укажите в нём тот же ингредиент и то же количество.
            </p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Модификатор</th>
                <th>Блюдо</th>
                <th className="num">Цена</th>
                <th className="num">Себестоимость</th>
                <th>Карта</th>
              </tr>
            </thead>
            <tbody>
              {costs.modifiers.map(modifier => (
                <tr key={modifier.optionId}>
                  <td>
                    <strong>{modifier.title}</strong>
                    {modifier.exclusion ? <small className="muted"> · исключение</small> : null}
                    <br />
                    <small className="muted">{modifier.groupTitle}</small>
                  </td>
                  <td className="muted">{modifier.productTitle}</td>
                  <td className="num">{modifier.price === 0 ? "—" : money(modifier.price)}</td>
                  <td className="num">{modifier.hasRecipe ? money(modifier.cost) : "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() =>
                        onEdit(
                          "modifier",
                          modifier.optionId,
                          modifier.title,
                          modifier.exclusion ? "что исключение убирает" : "что добавка добавляет"
                        )
                      }
                    >
                      {manage ? (modifier.hasRecipe ? "Изменить" : "Заполнить") : "Показать"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/**
 * Фудкост цветом.
 *
 * Пороги грубые и намеренно не настраиваются: для уличной еды 25–35 % — норма,
 * выше 40 % блюдо не окупает остальные расходы. Точные границы у каждой точки
 * свои, поэтому цвет здесь — подсказка, а не приговор.
 */
function FoodCostBadge({ value }: { value: number }) {
  const tone = value >= 40 ? "crit" : value >= 33 ? "warn" : "ok";
  return <span className={`food-cost food-cost-${tone}`}>{value.toLocaleString("ru-RU")} %</span>;
}

function BalancesTable({
  balances,
  filter,
  onFilter,
  onMovements
}: {
  balances: StockBalancesResponse | null;
  filter: "all" | "low" | "negative";
  onFilter: (filter: "all" | "low" | "negative") => void;
  onMovements: (ingredient: StockBalanceRow) => void;
}) {
  if (!balances || balances.items.length === 0) {
    return <Empty>Номенклатура пуста. Заведите сырьё во вкладке «Номенклатура».</Empty>;
  }

  const items = balances.items.filter(item =>
    filter === "negative" ? item.negative : filter === "low" ? item.low : true
  );

  return (
    <section className="panel">
      <header>
        <div>
          <h2>{balances.warehouse.title}</h2>
          <p>
            Красным помечен минус: техкарта расходует больше, чем приходовали, либо накладную ещё не занесли.
          </p>
        </div>
        {filter === "all" ? null : (
          <button type="button" className="btn btn-sm" onClick={() => onFilter("all")}>
            {filter === "negative" ? "Только минусы" : "Только заканчивающиеся"} · показать все
          </button>
        )}
      </header>

      {items.length === 0 ? (
        <Empty>
          {filter === "negative" ? "В минус не ушла ни одна позиция." : "Ничего не заканчивается."}
        </Empty>
      ) : (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th>Группа</th>
              <th className="num">Остаток</th>
              <th className="num">Себестоимость</th>
              <th className="num">Стоимость запаса</th>
              <th>Движение</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} className={item.negative ? "row-alert" : ""}>
                <td>
                  <strong>{item.title}</strong>
                  {item.kind === "prep" ? <small className="muted"> · заготовка</small> : null}
                </td>
                <td className="muted">{item.groupTitle ?? "—"}</td>
                <td className="num">
                  {formatQuantity(item.quantity, item.unit)}
                  {item.low ? (
                    <>
                      <br />
                      <small className="muted">ниже минимума {formatQuantity(item.minStock, item.unit)}</small>
                    </>
                  ) : null}
                </td>
                <td className="num">
                  {item.avgCost.toLocaleString("ru-RU", { maximumFractionDigits: 4 })} ₽/
                  {UNIT_TITLES[item.unit]}
                </td>
                <td className="num">{money(item.value)}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => onMovements(item)}>
                    Показать
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}

function DocumentsTable({
  documents,
  manage,
  onCancel,
  onEdit,
  onOpen
}: {
  documents: StockDocument[];
  manage: boolean;
  onCancel: (document: StockDocument) => void;
  onEdit: (document: StockDocument) => void;
  onOpen: (document: StockDocument) => void;
}) {
  if (documents.length === 0) return <Empty>Документов пока нет.</Empty>;

  return (
    <section className="panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Документ</th>
              <th>Дата</th>
              <th>Основание</th>
              <th className="num">Позиций</th>
              <th className="num">Сумма</th>
              <th>Автор</th>
              {manage ? <th>Действия</th> : null}
            </tr>
          </thead>
          <tbody>
            {documents.map(document => (
              <tr key={document.id} className={document.status === "cancelled" ? "row-muted" : ""}>
                <td>
                  <button type="button" className="btn-link" onClick={() => onOpen(document)}>
                    <strong>
                      {DOCUMENT_TITLES[document.type]} №{document.number}
                    </strong>
                  </button>
                  {document.status === "cancelled" ? (
                    <>
                      <br />
                      <small className="muted">отменён {dateTime(document.cancelledAt)}</small>
                    </>
                  ) : null}
                  {document.status === "pending" ? (
                    <>
                      <br />
                      <small className="muted">ждёт точку — остаток ещё не изменился</small>
                    </>
                  ) : null}
                </td>
                <td>{dateTime(document.date)}</td>
                <td>
                  {document.supplierTitle ??
                    WRITE_OFF_REASONS.find(reason => reason.id === document.reason)?.title ??
                    "—"}
                  {document.comment ? (
                    <>
                      <br />
                      <small className="muted">{document.comment}</small>
                    </>
                  ) : null}
                </td>
                <td className="num">
                  <button type="button" className="btn-link" onClick={() => onOpen(document)}>
                    {document.lines.length}
                  </button>
                </td>
                <td className="num">{money(document.amount)}</td>
                <td className="muted">{document.actor}</td>
                {manage ? (
                  <td>
                    {document.status === "posted" ? (
                      <div className="row-actions">
                        {/* Исправлять умеет только приход: у остальных
                            документов цену считает система, а не человек. */}
                        {document.type === "supply" ? (
                          <button type="button" className="btn btn-sm" onClick={() => onEdit(document)}>
                            Исправить
                          </button>
                        ) : null}
                        <button type="button" className="btn btn-sm" onClick={() => onCancel(document)}>
                          Отменить
                        </button>
                      </div>
                    ) : (
                      <span className="muted tiny">—</span>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Карточка документа.
 *
 * До сих пор в списке стояло только число позиций, и чтобы вспомнить, что
 * именно привезли по накладной, приходилось обходить движения по каждой
 * позиции. Документ — это то, с чем сверяются с бумагой, и открываться он
 * должен целиком.
 */
/**
 * Масса по карте: брутто, нетто и выход.
 *
 * Считается на сервере по сохранённой карте, поэтому после правки строк
 * показывает ещё прежние числа — об этом сказано прямо, иначе технолог решит,
 * что расчёт сломан.
 */
function RecipeWeightsPanel({ weights, saved }: { weights: RecipeWeights; saved: boolean }) {
  const gram = (value: number) => `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })} г`;

  return (
    <section className="metrics">
      <div className="metric">
        <b>{gram(weights.grossG)}</b>
        <span>масса брутто</span>
      </div>
      <div className="metric">
        <b>{gram(weights.netG)}</b>
        <span>нетто после обработки</span>
      </div>
      <div className="metric">
        <b>{gram(weights.outputG)}</b>
        <span>выход в готовом виде</span>
      </div>
      {weights.unweighed.length > 0 ? (
        <div className="metric metric-alert">
          <b>{weights.unweighed.length}</b>
          {/*
            Штучная позиция без веса штуки в массу не попала, и выход занижен.
            Молча пропустить её значило бы выдать неполный расчёт за полный.
          */}
          <span>без веса штуки: {weights.unweighed.join(", ")}</span>
        </div>
      ) : null}
      {saved ? null : (
        <div className="metric">
          <b>—</b>
          <span>масса пересчитается после сохранения</span>
        </div>
      )}
    </section>
  );
}

function DocumentCard({ document, onClose }: { document: StockDocument; onClose: () => void }) {
  const isInventory = document.type === "inventory";
  const isSupply = document.type === "supply";

  return (
    <Modal
      title={`${DOCUMENT_TITLES[document.type]} №${document.number}`}
      size="wide"
      onClose={onClose}
      actions={
        <button type="button" className="btn" onClick={onClose}>
          Закрыть
        </button>
      }
    >
      <dl className="doc-head">
        <div>
          <dt>Дата</dt>
          <dd>{dateTime(document.date)}</dd>
        </div>
        <div>
          <dt>Склад</dt>
          <dd>{document.warehouseTitle}</dd>
        </div>
        <div>
          <dt>Основание</dt>
          <dd>
            {document.supplierTitle ??
              WRITE_OFF_REASONS.find(reason => reason.id === document.reason)?.title ??
              "—"}
          </dd>
        </div>
        <div>
          <dt>Провёл</dt>
          <dd>{document.actor}</dd>
        </div>
        {document.producedIngredientTitle ? (
          <div>
            <dt>Приготовлено</dt>
            <dd>
              {document.producedIngredientTitle}
              {document.producedQuantity === null ? "" : ` · ${document.producedQuantity}`}
            </dd>
          </div>
        ) : null}
        {document.status === "cancelled" ? (
          <div>
            <dt>Отменён</dt>
            <dd>
              {dateTime(document.cancelledAt)}
              {document.cancelledBy ? ` · ${document.cancelledBy}` : ""}
            </dd>
          </div>
        ) : null}
        {document.status === "pending" ? (
          <div>
            <dt>Состояние</dt>
            <dd>
              Ждёт точку
              <br />
              <small className="muted">
                Остаток и себестоимость изменятся, когда накладную проведут в фудтраке.
              </small>
            </dd>
          </div>
        ) : null}
      </dl>

      {document.comment ? <p className="hint">{document.comment}</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              {isInventory ? <th className="num">Расчёт</th> : null}
              <th className="num">{isInventory ? "Факт" : "Количество"}</th>
              {isInventory ? <th className="num">Разница</th> : null}
              <th className="num">Цена</th>
              <th className="num">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {document.lines.map(line => (
              <tr key={line.id}>
                <td>{line.ingredientTitle}</td>
                {isInventory ? (
                  <td className="num">
                    {line.expectedQuantity === null
                      ? "—"
                      : formatQuantity(line.expectedQuantity, line.unit)}
                  </td>
                ) : null}
                <td className="num">
                  {/* Как ввели, так и показываем: «2 кг», а не «2000 г». */}
                  {line.entryQuantity} {line.entryUnitTitle}
                </td>
                {isInventory ? (
                  <td className="num">{formatQuantity(line.quantity, line.unit)}</td>
                ) : null}
                <td className="num">
                  {line.entryPrice.toLocaleString("ru-RU", { maximumFractionDigits: 4 })} ₽/
                  {UNIT_TITLES[line.priceUnit] ?? line.priceUnit}
                </td>
                <td className="num">{money(line.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={isInventory ? 5 : 3}>
                <strong>Итого</strong>
              </td>
              <td className="num">
                <strong>{money(document.amount)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="hint">
        {isSupply
          ? "Цена — из накладной, в той единице, в которой её выписали. Себестоимость на складе пересчитана в базовые единицы."
          : "Цена — средняя себестоимость на момент проведения, а не закупочная."}
      </p>
    </Modal>
  );
}

/**
 * Документы одного поставщика.
 *
 * Вопрос «что мы у них брали и на сколько» задают о поставщике, а не о дате,
 * и искать это построчно в общем списке — работа на вечер.
 */
function SupplierDocumentsModal({
  supplier,
  documents,
  onClose,
  onOpen
}: {
  supplier: StockSupplier;
  documents: StockDocument[];
  onClose: () => void;
  onOpen: (document: StockDocument) => void;
}) {
  // Отменённые в сумму не входят: их деньги вернулись обратными движениями.
  const total = documents
    .filter(document => document.status === "posted")
    .reduce((sum, document) => sum + document.amount, 0);

  return (
    <Modal
      title={supplier.title}
      onClose={onClose}
      actions={
        <button type="button" className="btn" onClick={onClose}>
          Закрыть
        </button>
      }
    >
      {documents.length === 0 ? (
        <Empty>Документов по этому поставщику пока нет.</Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Документ</th>
                  <th>Дата</th>
                  <th className="num">Позиций</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(document => (
                  <tr key={document.id} className={document.status === "cancelled" ? "row-muted" : ""}>
                    <td>
                      <button type="button" className="btn-link" onClick={() => onOpen(document)}>
                        {DOCUMENT_TITLES[document.type]} №{document.number}
                      </button>
                    </td>
                    <td>{dateTime(document.date)}</td>
                    <td className="num">{document.lines.length}</td>
                    <td className="num">{money(document.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>
                    <strong>Итого проведено</strong>
                  </td>
                  <td className="num">
                    <strong>{money(total)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function CatalogTable({
  catalog,
  manage,
  onCreate,
  onEdit,
  onEditRecipe,
  onProduce,
  onReload
}: {
  catalog: StockIngredientsResponse | null;
  manage: boolean;
  onCreate: () => void;
  onEdit: (ingredient: StockIngredient) => void;
  onEditRecipe: (ingredient: StockIngredient) => void;
  onProduce: (ingredient: StockIngredient) => void;
  onReload: () => Promise<void>;
}) {
  const { run } = useToasts();
  const { snapshot } = useAppState();
  const [query, setQuery] = useState("");

  // Приготовление заготовки — операция у плиты: в облаке её не проводят.
  const postsHere = snapshot?.settings.appRole !== "cloud";

  /*
   * Поиск по названию. Шестьдесят шесть позиций глазами не просматривают:
   * чтобы поправить вес котлеты, приходилось листать список до мяса.
   */
  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || !catalog) return catalog?.items ?? [];
    return catalog.items.filter(item => item.title.toLowerCase().includes(needle));
  }, [catalog, query]);

  if (!catalog) return null;

  async function archive(ingredient: StockIngredient) {
    const ok = await run(
      () => api.patch(`/api/admin/stock/ingredients/${ingredient.id}`, { archived: true }),
      `«${ingredient.title}» в архиве`
    );
    if (ok) await onReload();
  }

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Номенклатура</h2>
          <p>Единицу учёта менять нельзя — в ней записаны остатки и вся история движений.</p>
        </div>
        <div className="row-actions">
          <input
            className="picker-input"
            value={query}
            placeholder="Поиск по названию"
            onChange={event => setQuery(event.target.value)}
          />
          {manage ? (
            <button type="button" className="btn btn-sm btn-primary" onClick={onCreate}>
              + Позиция
            </button>
          ) : null}
        </div>
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th>Группа</th>
              <th>Единица</th>
              <th>Вид</th>
              <th className="num">Минимум</th>
              <th className="num">Вес штуки</th>
              {manage ? <th>Действия</th> : null}
            </tr>
          </thead>
          <tbody>
            {found.map(ingredient => (
              <tr key={ingredient.id}>
                <td>
                  <strong>{ingredient.title}</strong>
                  <br />
                  <code className="tiny">{ingredient.slug}</code>
                </td>
                <td className="muted">{ingredient.groupTitle ?? "—"}</td>
                <td>{ingredient.unitTitle}</td>
                <td>{ingredient.kind === "prep" ? "Заготовка" : "Сырьё"}</td>
                <td className="num">
                  {ingredient.minStock > 0 ? formatQuantity(ingredient.minStock, ingredient.unit) : "—"}
                </td>
                <td className="num">
                  {ingredient.unit !== "pcs" ? (
                    <span className="muted">—</span>
                  ) : ingredient.pieceWeightG ? (
                    `${ingredient.pieceWeightG} г`
                  ) : (
                    <small className="muted">не задан</small>
                  )}
                </td>
                {manage ? (
                  <td>
                    <div className="row-actions">
                      {ingredient.kind === "prep" ? (
                        <>
                          <button type="button" className="btn btn-sm" onClick={() => onEditRecipe(ingredient)}>
                            Закладка
                          </button>
                          {postsHere ? (
                            <button type="button" className="btn btn-sm" onClick={() => onProduce(ingredient)}>
                              Приготовить
                            </button>
                          ) : null}
                        </>
                      ) : null}
                      <button type="button" className="btn btn-sm" onClick={() => onEdit(ingredient)}>
                        Изменить
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => void archive(ingredient)}>
                        В архив
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SuppliersTable({
  suppliers,
  documents,
  manage,
  onCreate,
  onOpen
}: {
  suppliers: StockSupplier[];
  documents: StockDocument[];
  manage: boolean;
  onCreate: () => void;
  onOpen: (supplier: StockSupplier) => void;
}) {
  return (
    <section className="panel">
      <header>
        <div>
          <h2>Поставщики</h2>
        </div>
        {manage ? (
          <button type="button" className="btn btn-sm btn-primary" onClick={onCreate}>
            + Поставщик
          </button>
        ) : null}
      </header>

      {suppliers.length === 0 ? (
        <Empty>Поставщиков пока нет.</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>ИНН</th>
                <th>Телефон</th>
                <th className="num">Документов</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(supplier => (
                <tr key={supplier.id}>
                  <td>
                    <button type="button" className="btn-link" onClick={() => onOpen(supplier)}>
                      <strong>{supplier.title}</strong>
                    </button>
                  </td>
                  <td className="muted">{supplier.inn ?? "—"}</td>
                  <td className="muted">{supplier.phone ?? "—"}</td>
                  <td className="num">
                    {documents.filter(document => document.supplierId === supplier.id).length}
                  </td>
                  <td className="muted">{supplier.comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MovementsTable({ ingredient, items }: { ingredient: StockIngredient; items: StockMovement[] }) {
  const TYPE_TITLES: Record<StockMovement["type"], string> = {
    supply: "Приход",
    sale: "Продажа",
    sale_return: "Возврат по заказу",
    writeoff: "Списание",
    supplier_return: "Возврат поставщику",
    production_out: "В заготовку",
    production_in: "Из заготовки",
    processing_out: "В переработку",
    processing_in: "Из переработки",
    inventory: "Инвентаризация"
  };

  if (items.length === 0) return <Empty>Движений пока не было.</Empty>;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            <th>Операция</th>
            <th className="num">Количество</th>
            <th className="num">Остаток после</th>
            <th className="num">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {items.map(movement => (
            <tr key={movement.id} className={movement.reversal ? "row-muted" : ""}>
              <td>{dateTime(movement.at)}</td>
              <td>
                {TYPE_TITLES[movement.type]}
                {movement.reversal ? <small className="muted"> · отмена</small> : null}
                <br />
                <small className="muted">
                  {movement.documentNumber
                    ? `документ №${movement.documentNumber}`
                    : movement.orderNumber
                      ? `заказ №${movement.orderNumber}`
                      : movement.actor}
                </small>
              </td>
              <td className="num">
                {movement.quantity > 0 ? "+" : ""}
                {formatQuantity(movement.quantity, ingredient.unit)}
              </td>
              <td className="num">{formatQuantity(movement.balanceAfter, ingredient.unit)}</td>
              <td className="num">{money(movement.cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Приход, списание и возврат поставщику — одна форма на три документа.
 *
 * Отличаются они немногим: у прихода есть цены, у списания причина, у возврата
 * поставщик и ни того ни другого. Разводить их по трём почти одинаковым
 * компонентам значило бы трижды чинить одну и ту же ошибку в подстановке
 * единиц измерения.
 */
function DocumentModal({
  title,
  ingredients,
  suppliers,
  kind,
  edit,
  onClose,
  onSaved
}: {
  title: string;
  /** Исправляемая накладная. Не задана — заводится новая. */
  edit?: StockDocument | null;
  ingredients: StockIngredient[];
  suppliers: StockSupplier[];
  kind: "supply" | "writeoff" | "return";
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const withPrice = kind === "supply";
  const withSupplier = kind === "supply" || kind === "return";
  const withReason = kind === "writeoff";
  const { run } = useToasts();

  const [lines, setLines] = useState<StockDocumentLineInput[]>(
    () =>
      edit?.lines.map(line => ({
        ingredientId: line.ingredientId,
        quantity: line.entryQuantity,
        unit: line.entryUnit,
        price: line.entryPrice,
        priceUnit: line.priceUnit
      })) ?? [emptyLine()]
  );
  const [supplierId, setSupplierId] = useState(edit?.supplierId ?? "");
  const [reason, setReason] = useState<WriteOffReason>("spoilage");
  const [comment, setComment] = useState(edit?.comment ?? "");
  /*
   * Дата документа. Накладную заносят задним числом чаще, чем в тот же день:
   * товар приняли вечером, а бухгалтер сел за него утром. Без поля документ
   * ложился сегодняшним днём, и отчёт за вчера не сходился с накладной.
   */
  const [date, setDate] = useState(
    () => (edit ? new Date(edit.date) : new Date()).toISOString().slice(0, 10)
  );
  const [busy, setBusy] = useState(false);

  const byId = useMemo(() => new Map(ingredients.map(item => [item.id, item])), [ingredients]);

  /*
   * Позиции из исправляемой накладной, которых больше нет в справочнике.
   *
   * Номенклатуру архивируют, а накладные остаются: строка со вчерашней
   * котлетой должна быть видна, иначе форма молча покажет пустой список и
   * кладовщик решит, что документ пустой. Провести такую строку сервер не
   * даст — архивная позиция на склад не приходуется, — и скажет об этом
   * словами.
   */
  const archivedLines = useMemo(
    () =>
      (edit?.lines ?? [])
        .filter(line => !byId.has(line.ingredientId))
        .map(line => ({ id: line.ingredientId, title: line.ingredientTitle, unit: line.entryUnit })),
    [edit, byId]
  );

  /** Список для поиска: единица учёта подсказывает, та ли это позиция. */
  const pickerItems = useMemo<PickerItem[]>(
    () => [
      ...archivedLines.map(item => ({ id: item.id, title: item.title, note: "в архиве" })),
      ...ingredients.map(item => ({
        id: item.id,
        title: item.title,
        note: item.groupTitle ? `${item.unitTitle} · ${item.groupTitle}` : item.unitTitle
      }))
    ],
    [archivedLines, ingredients]
  );
  const total = lines.reduce((sum, line) => sum + lineAmount(line, byId.get(line.ingredientId)), 0);
  const filled = lines.filter(line => line.ingredientId && line.quantity > 0);

  function update(index: number, patch: Partial<StockDocumentLineInput>) {
    setLines(current =>
      current.map((line, position) => {
        if (position !== index) return line;
        const next = { ...line, ...patch };

        // Смена позиции подставляет её единицу: у грамма и литра списки разные,
        // и оставлять прежнюю единицу — верный способ получить отказ сервера.
        if (patch.ingredientId) {
          const ingredient = byId.get(patch.ingredientId);
          next.unit = ingredient?.entryUnits[0] ?? "";
          next.priceUnit = ingredient?.priceUnits[0] ?? next.unit;
        }

        // Смена единицы количества уводит за собой и единицу цены, пока её
        // не выбрали отдельно: обычно накладную выписывают в одних единицах.
        if (patch.unit && !patch.priceUnit) {
          const ingredient = byId.get(next.ingredientId);
          if (ingredient && !ingredient.priceUnits.includes(next.priceUnit ?? "")) {
            next.priceUnit = patch.unit;
          }
        }
        return next;
      })
    );
  }

  async function submit() {
    setBusy(true);

    const rows = filled.map(line => ({
      ingredientId: line.ingredientId,
      quantity: line.quantity,
      // Вес — способ ввода, а не единица учёта: на склад уходят штуки,
      // как и записано в номенклатуре.
      unit: line.unit,
      ...(withPrice ? { price: line.price, priceUnit: line.priceUnit || line.unit } : {})
    }));

    const payload = {
      comment: comment.trim() || undefined,
      // Время берётся полуднем: дата важна, час — нет, а полночь у границы
      // суток легко уезжает во вчера при пересчёте часовых поясов.
      date: `${date}T12:00:00`,
      lines: rows,
      ...(withSupplier ? { supplierId: supplierId || null } : {}),
      ...(withReason ? { reason } : {})
    };

    const endpoint =
      kind === "supply"
        ? "/api/admin/stock/supplies"
        : kind === "return"
          ? "/api/admin/stock/returns"
          : "/api/admin/stock/writeoffs";

    const done =
      kind === "supply" ? "Накладная проведена" : kind === "return" ? "Возврат проведён" : "Списание проведено";

    /*
     * Исправление идёт своим запросом: сервер отменяет прежнюю накладную и
     * проводит новую одной транзакцией. Двумя запросами с этого экрана можно
     * было бы остаться с отменённым документом и без нового.
     */
    const ok = edit
      ? await run(() => api.put(`/api/admin/stock/supplies/${edit.id}`, payload), "Накладная исправлена")
      : await run(() => api.once.post(endpoint, payload), done);

    setBusy(false);
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={title}
      size="wide"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={filled.length === 0 || busy}
            onClick={() => void submit()}
          >
            {busy ? "Сохраняем…" : edit ? "Сохранить" : "Провести"}
          </button>
        </>
      }
    >
      <div className="field-row">
        <label className="field">
          <span>Дата документа</span>
          <input type="date" value={date} onChange={event => setDate(event.target.value)} />
          <small className="muted">
            День, когда товар пришёл, а не когда его занесли. Отчёты считают по этой дате.
          </small>
        </label>
      </div>

      {withSupplier ? (
        <label className="field">
          <span>Поставщик</span>
          <select value={supplierId} onChange={event => setSupplierId(event.target.value)}>
            <option value="">Без поставщика</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {withReason ? (
        <label className="field">
          <span>Причина</span>
          <select value={reason} onChange={event => setReason(event.target.value as WriteOffReason)}>
            {WRITE_OFF_REASONS.map(item => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th>Ввод</th>
              <th className="num">Количество</th>
              <th>Единица</th>
              {withPrice ? <th className="num">Цена</th> : null}
              {withPrice ? <th>За единицу</th> : null}
              {withPrice ? <th className="num">Сумма</th> : null}
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const ingredient = byId.get(line.ingredientId);

              return (
                <tr key={index}>
                  <td>
                    <IngredientPicker
                      items={pickerItems}
                      value={line.ingredientId}
                      onChange={id => update(index, { ingredientId: id })}
                    />
                  </td>
                  <td>
                    {/*
                      Переключатель появляется только там, где пересчитывать
                      есть чем: у штучной позиции с заданным весом штуки.
                      У остальных он был бы кнопкой, которая ничего не делает.
                    */}
                    {ingredient?.unit === "pcs" && ingredient.pieceWeightG ? (
                      <label className="check tiny">
                        <input
                          type="checkbox"
                          checked={Boolean(line.byWeight)}
                          onChange={event =>
                            update(index, {
                              byWeight: event.target.checked,
                              weightUnit: line.weightUnit || "kg",
                              quantity: 0,
                              weight: 0
                            })
                          }
                        />
                        весом
                      </label>
                    ) : (
                      <span className="muted tiny">штуками</span>
                    )}
                  </td>
                  <td className="num">
                    {line.byWeight ? (
                      <>
                        <input
                          type="number"
                          min={0}
                          step="0.001"
                          value={line.weight || ""}
                          onChange={event => {
                            const weight = Number(event.target.value);
                            update(index, {
                              weight,
                              quantity: piecesFromWeight(
                                weight,
                                line.weightUnit || "kg",
                                ingredient?.pieceWeightG ?? null
                              )
                            });
                          }}
                        />
                        <small className="muted">= {line.quantity || 0} шт</small>
                      </>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        step="0.001"
                        value={line.quantity || ""}
                        onChange={event => update(index, { quantity: Number(event.target.value) })}
                      />
                    )}
                  </td>
                  <td>
                    {line.byWeight ? (
                      <select
                        value={line.weightUnit || "kg"}
                        onChange={event => {
                          const weightUnit = event.target.value;
                          update(index, {
                            weightUnit,
                            quantity: piecesFromWeight(
                              line.weight ?? 0,
                              weightUnit,
                              ingredient?.pieceWeightG ?? null
                            )
                          });
                        }}
                      >
                        <option value="kg">кг</option>
                        <option value="g">г</option>
                      </select>
                    ) : (
                    <select
                      value={line.unit}
                      disabled={!ingredient}
                      onChange={event => update(index, { unit: event.target.value })}
                    >
                      {(ingredient?.entryUnits ?? [line.unit].filter(Boolean)).map(unit => (
                        <option key={unit} value={unit}>
                          {UNIT_TITLES[unit] ?? unit}
                        </option>
                      ))}
                    </select>
                    )}
                  </td>
                  {withPrice ? (
                    <td className="num">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.price || ""}
                        onChange={event => update(index, { price: Number(event.target.value) })}
                      />
                    </td>
                  ) : null}
                  {withPrice ? (
                    <td>
                      {/*
                        Отдельно от единицы количества: котлеты принимают
                        штуками, а накладную поставщик выписывает за килограммы.
                        Список приходит с сервера — килограмм в нём появляется
                        только у штучного товара с заданным весом штуки.
                      */}
                      <select
                        value={line.priceUnit || line.unit}
                        disabled={!ingredient}
                        onChange={event => update(index, { priceUnit: event.target.value })}
                      >
                        {(ingredient?.priceUnits ?? [line.priceUnit || line.unit].filter(Boolean)).map(unit => (
                          <option key={unit} value={unit}>
                            за {UNIT_TITLES[unit] ?? unit}
                          </option>
                        ))}
                      </select>
                    </td>
                  ) : null}
                  {withPrice ? <td className="num">{money(lineAmount(line, ingredient))}</td> : null}
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setLines(current => current.filter((_, position) => position !== index))}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="row-actions">
        <button type="button" className="btn btn-sm" onClick={() => setLines(current => [...current, emptyLine()])}>
          + Строка
        </button>
        {withPrice ? <strong className="ml-auto">Итого: {money(total)}</strong> : null}
      </div>

      <label className="field">
        <span>Комментарий</span>
        <input value={comment} onChange={event => setComment(event.target.value)} maxLength={500} />
      </label>

      {!withPrice ? (
        <p className="hint">
          Сумма списания посчитается сама — по средней себестоимости на момент проведения.
        </p>
      ) : null}
    </Modal>
  );
}

/**
 * Карточка позиции номенклатуры — заведение и правка одной формой.
 *
 * Единица учёта при правке заблокирована: в ней записаны остатки и вся
 * история движений, и подмена «г» на «шт» превратила бы пятьсот граммов
 * в пятьсот штук. Сервер это тоже не пропустит — здесь поле выключено,
 * чтобы отказ не приходил после заполнения всей формы.
 */
function IngredientModal({
  ingredient,
  groups,
  onClose,
  onSaved
}: {
  ingredient: StockIngredient | null;
  groups: { id: string; title: string }[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [title, setTitle] = useState(ingredient?.title ?? "");
  const [unit, setUnit] = useState<"g" | "ml" | "pcs">(ingredient?.unit ?? "g");
  const [kind, setKind] = useState<"raw" | "prep">(ingredient?.kind ?? "raw");
  const [groupId, setGroupId] = useState(ingredient?.groupId ?? "");
  const [minStock, setMinStock] = useState(ingredient?.minStock ?? 0);
  const [prepOutput, setPrepOutput] = useState(ingredient?.prepOutput ?? 0);
  const [pieceWeightG, setPieceWeightG] = useState(ingredient?.pieceWeightG ?? 0);

  async function submit() {
    const body = {
      title: title.trim(),
      kind,
      groupId: groupId || null,
      minStock,
      prepOutput: kind === "prep" && prepOutput > 0 ? prepOutput : null,
      pieceWeightG: unit === "pcs" && pieceWeightG > 0 ? pieceWeightG : null
    };

    const ok = await run(
      () =>
        ingredient
          ? api.patch(`/api/admin/stock/ingredients/${ingredient.id}`, body)
          : api.post("/api/admin/stock/ingredients", { ...body, unit }),
      ingredient ? "Позиция сохранена" : "Позиция добавлена"
    );
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={ingredient ? `Позиция «${ingredient.title}»` : "Новая позиция номенклатуры"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!title.trim()}
            onClick={() => void submit()}
          >
            {ingredient ? "Сохранить" : "Добавить"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input value={title} onChange={event => setTitle(event.target.value)} maxLength={200} autoFocus />
      </label>

      <label className="field">
        <span>Единица учёта</span>
        <select
          value={unit}
          disabled={Boolean(ingredient)}
          onChange={event => setUnit(event.target.value as "g" | "ml" | "pcs")}
        >
          <option value="g">Граммы (приход можно в кг)</option>
          <option value="ml">Миллилитры (приход можно в литрах)</option>
          <option value="pcs">Штуки</option>
        </select>
      </label>

      <label className="field">
        <span>Вид</span>
        <select value={kind} onChange={event => setKind(event.target.value as "raw" | "prep")}>
          <option value="raw">Сырьё — закупается</option>
          <option value="prep">Заготовка — готовится на кухне</option>
        </select>
      </label>

      <label className="field">
        <span>Группа</span>
        <select value={groupId} onChange={event => setGroupId(event.target.value)}>
          <option value="">Без группы</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.title}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Неснижаемый остаток</span>
        <input
          type="number"
          min={0}
          step="1"
          value={minStock || ""}
          onChange={event => setMinStock(Number(event.target.value))}
        />
      </label>

      {unit === "pcs" ? (
        <label className="field">
          <span>Вес одной штуки, г</span>
          <input
            type="number"
            min={0}
            step="0.001"
            value={pieceWeightG || ""}
            onChange={event => setPieceWeightG(Number(event.target.value))}
          />
          <small className="muted">
            Нужен там, где штуки и килограммы должны сойтись: цена в накладной за килограмм и масса набора
            в технологической карте. Без него ни то, ни другое не посчитается.
          </small>
        </label>
      ) : null}

      {kind === "prep" ? (
        <label className="field">
          <span>Выход одной закладки, {UNIT_TITLES[unit]}</span>
          <input
            type="number"
            min={0}
            step="1"
            value={prepOutput || ""}
            onChange={event => setPrepOutput(Number(event.target.value))}
          />
        </label>
      ) : null}

      <p className="hint">
        Единицу учёта потом не поменять: в ней будут записаны остатки и вся история движений.
        {kind === "prep"
          ? " Выход закладки — сколько получается из одной техкарты; без него акт приготовления не проводится."
          : ""}
      </p>
    </Modal>
  );
}

function RecipeModal({
  owner,
  ownerId,
  title,
  subtitle,
  ingredients,
  manage,
  onClose,
  onSaved
}: {
  owner: RecipeOwnerKind;
  ownerId: string;
  title: string;
  subtitle: string;
  ingredients: StockIngredient[];
  manage: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run, notify } = useToasts();

  const [rows, setRows] = useState<RecipeRowInput[]>([]);
  const [heatLossPercent, setHeatLossPercent] = useState(0);
  const [weights, setWeights] = useState<RecipeWeights | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const byId = useMemo(() => new Map(ingredients.map(item => [item.id, item])), [ingredients]);

  /** Тот же поиск, что и в накладной: позиций шестьдесят шесть. */
  const recipePickerItems = useMemo<PickerItem[]>(
    () =>
      ingredients.map(item => ({
        id: item.id,
        title: item.title,
        note: item.kind === "prep" ? `${item.unitTitle} · заготовка` : item.unitTitle
      })),
    [ingredients]
  );


  useEffect(() => {
    let cancelled = false;

    api
      .get<{ rows: RecipeRowInput[]; weights: RecipeWeights }>(
        `/api/admin/stock/recipes/${owner}/${ownerId}`
      )
      .then(data => {
        if (cancelled) return;
        setRows(
          data.rows.map(row => ({
            ingredientId: row.ingredientId,
            quantity: row.quantity,
            coldLossPercent: row.coldLossPercent
          }))
        );
        setWeights(data.weights);
        setHeatLossPercent(data.weights.heatLossPercent);
      })
      .catch(error => {
        if (!cancelled) notify(error instanceof Error ? error.message : "Не удалось загрузить карту", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [owner, ownerId, notify]);

  async function submit() {
    setBusy(true);
    const ok = await run(
      () =>
        api
          .put<{ weights: RecipeWeights }>(`/api/admin/stock/recipes/${owner}/${ownerId}`, {
            rows: rows.filter(row => row.ingredientId && row.quantity > 0),
            // Ужарка бывает только у блюда: у добавки и заготовки её нет.
            ...(owner === "product" ? { heatLossPercent } : {})
          })
          // Масса пересчитывается сервером по сохранённой карте, а не здесь:
          // на экране она должна совпадать с той, что попадёт в калькуляцию.
          .then(data => setWeights(data.weights)),
      "Техкарта сохранена"
    );
    setBusy(false);
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={`${title} · ${subtitle}`}
      size="wide"
      onClose={onClose}
      actions={
        manage ? (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Отмена
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
              {busy ? "Сохраняем…" : "Сохранить"}
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
        )
      }
    >
      {loading ? (
        <Empty>Загрузка карты…</Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Брутто на порцию</th>
                  <th>Единица</th>
                  <th className="num">Отход, %</th>
                  {manage ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const ingredient = byId.get(row.ingredientId);

                  return (
                    <tr key={index}>
                      <td>
                        <IngredientPicker
                          items={recipePickerItems}
                          value={row.ingredientId}
                          disabled={!manage}
                          onChange={id =>
                            setRows(current =>
                              current.map((item, position) =>
                                position === index ? { ...item, ingredientId: id } : item
                              )
                            )
                          }
                        />
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          min={0}
                          step="0.001"
                          disabled={!manage}
                          value={row.quantity || ""}
                          onChange={event =>
                            setRows(current =>
                              current.map((item, position) =>
                                position === index ? { ...item, quantity: Number(event.target.value) } : item
                              )
                            )
                          }
                        />
                      </td>
                      <td>{ingredient?.unitTitle ?? "—"}</td>
                      <td className="num">
                        {/*
                          Отход при холодной обработке: чистка, обрезка, разделка.
                          Со склада уходит брутто — картофель забирают неочищенным,
                          и списывается именно он.
                        */}
                        <input
                          type="number"
                          min={0}
                          max={99.99}
                          step="0.1"
                          disabled={!manage}
                          value={row.coldLossPercent || ""}
                          placeholder="0"
                          onChange={event =>
                            setRows(current =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...item, coldLossPercent: Number(event.target.value) }
                                  : item
                              )
                            )
                          }
                        />
                      </td>
                      {manage ? (
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setRows(current => current.filter((_, position) => position !== index))}
                          >
                            ×
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length === 0 ? <Empty>Карта пуста — сырьё по этой позиции не списывается.</Empty> : null}

          {manage ? (
            <div className="row-actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  setRows(current => [...current, { ingredientId: "", quantity: 0, coldLossPercent: 0 }])
                }
              >
                + Строка
              </button>
            </div>
          ) : null}

          {owner === "product" ? (
            <label className="field">
              <span>Потери при тепловой обработке, %</span>
              <input
                type="number"
                min={0}
                max={99.99}
                step="0.1"
                disabled={!manage}
                value={heatLossPercent || ""}
                placeholder="0"
                onChange={event => setHeatLossPercent(Number(event.target.value))}
              />
              <small className="muted">
                Ужарка, уварка, испарение — общие для всей карты. Отход в строках происходит до плиты, эти потери —
                на ней.
              </small>
            </label>
          ) : null}

          {weights ? <RecipeWeightsPanel weights={weights} saved={heatLossPercent === weights.heatLossPercent} /> : null}

          <p className="hint">
            Брутто указывается в базовой единице позиции на одну порцию — столько уходит со склада. Пустая карта
            сохраняется: это способ отключить списание, не удаляя блюдо.
          </p>
        </>
      )}
    </Modal>
  );
}

function ProductionModal({
  prep,
  onClose,
  onSaved
}: {
  prep: StockIngredient;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [quantity, setQuantity] = useState(0);
  const [unit, setUnit] = useState(prep.entryUnits[0] ?? "");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    const ok = await run(
      () =>
        api.once.post("/api/admin/stock/productions", {
          ingredientId: prep.id,
          quantity,
          unit,
          comment: comment.trim() || undefined
        }),
      `${prep.title}: акт приготовления проведён`
    );
    setBusy(false);
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={`Приготовить: ${prep.title}`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={quantity <= 0 || busy}
            onClick={() => void submit()}
          >
            {busy ? "Проводим…" : "Провести"}
          </button>
        </>
      }
    >
      <div className="field-row">
        <label className="field">
          <span>Сколько получилось</span>
          <input
            type="number"
            min={0}
            step="0.001"
            value={quantity || ""}
            onChange={event => setQuantity(Number(event.target.value))}
            autoFocus
          />
        </label>
        <label className="field">
          <span>Единица</span>
          <select value={unit} onChange={event => setUnit(event.target.value)}>
            {prep.entryUnits.map(code => (
              <option key={code} value={code}>
                {UNIT_TITLES[code] ?? code}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="field">
        <span>Комментарий</span>
        <input value={comment} onChange={event => setComment(event.target.value)} maxLength={500} />
      </label>

      <p className="hint">
        {prep.prepOutput === null ? (
          <strong>
            У заготовки не задан выход закладки — приготовление не проведётся. Укажите, сколько получается из одной
            закладки.
          </strong>
        ) : (
          <>
            Одна закладка даёт {formatQuantity(prep.prepOutput, prep.unit)}. Сырьё спишется по техкарте, пересчитанной
            на указанный объём, а себестоимость заготовки сложится из того, что реально ушло со склада.
          </>
        )}
      </p>
    </Modal>
  );
}

function SupplierModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { run } = useToasts();

  const [title, setTitle] = useState("");
  const [inn, setInn] = useState("");
  const [phone, setPhone] = useState("");

  async function submit() {
    const ok = await run(
      () =>
        api.post("/api/admin/stock/suppliers", {
          title: title.trim(),
          inn: inn.trim() || null,
          phone: phone.trim() || null
        }),
      "Поставщик добавлен"
    );
    if (ok) await onSaved();
  }

  return (
    <Modal
      title="Новый поставщик"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!title.trim()}
            onClick={() => void submit()}
          >
            Добавить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input value={title} onChange={event => setTitle(event.target.value)} maxLength={200} autoFocus />
      </label>
      <label className="field">
        <span>ИНН</span>
        <input value={inn} onChange={event => setInn(event.target.value)} maxLength={20} />
      </label>
      <label className="field">
        <span>Телефон</span>
        <input value={phone} onChange={event => setPhone(event.target.value)} maxLength={40} />
      </label>
    </Modal>
  );
}

/**
 * Закрытие складского периода.
 *
 * После сведения месяца документы задним числом в него уже не проводятся и не
 * отменяются: себестоимость, по которой посчитан прошлый фудкост, не должна
 * меняться после того, как по ней приняли решения — подняли цену, сменили
 * поставщика.
 *
 * Продаж это не касается: они идут сегодняшним днём. Заблокировать кассу из-за
 * сведённого месяца было бы худшим из возможных последствий бухгалтерской
 * дисциплины.
 */
function PeriodPanel() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { run } = useToasts();

  const closedUntil = snapshot?.settings.stockClosedUntil ?? null;
  const [value, setValue] = useState("");

  if (!can("settings.manage")) {
    return closedUntil ? (
      <section className="panel">
        <header>
          <div>
            <h2>Период закрыт</h2>
            <p>Документы по {dateTime(closedUntil)} провести и отменить нельзя.</p>
          </div>
        </header>
      </section>
    ) : null;
  }

  async function apply(next: string | null) {
    await run(
      () =>
        api.patch("/api/admin/stock/period", {
          // Конец дня, а не начало: закрывая месяц, закрывают его целиком.
          closedUntil: next === null ? null : new Date(`${next}T23:59:59`).toISOString()
        }),
      next === null ? "Период открыт" : "Период закрыт"
    );
  }

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Закрытие периода</h2>
          <p>
            {closedUntil
              ? `Склад закрыт по ${dateTime(closedUntil)}. Документы этой датой и раньше не проводятся и не отменяются.`
              : "Склад открыт: документы можно проводить любой датой."}
          </p>
        </div>
      </header>

      <div className="filters">
        <label className="field">
          <span>Закрыть по дату включительно</span>
          <input type="date" value={value} onChange={event => setValue(event.target.value)} />
        </label>

        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!value}
          onClick={() => void apply(value)}
        >
          Закрыть период
        </button>

        {closedUntil ? (
          <button type="button" className="btn btn-sm" onClick={() => void apply(null)}>
            Открыть обратно
          </button>
        ) : null}
      </div>

      <p className="tiny muted">
        Открыть обратно можно — ошибиться при закрытии проще, чем при вводе накладной, и запертый
        навсегда месяц обошёлся бы дороже. Продажи закрытие не трогает: они идут сегодняшним днём.
      </p>
    </section>
  );
}

/**
 * Акт переработки: одно сырьё разбирается на несколько выходов.
 *
 * Форма отдельная от прихода и списания, потому что документ здесь не список
 * строк, а превращение: слева то, что разобрали, справа то, что получилось.
 * Сложить их в одну таблицу значило бы просить кладовщика отличать вход от
 * выхода по знаку количества.
 */
function ProcessingModal({
  ingredients,
  onClose,
  onSaved
}: {
  ingredients: StockIngredient[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [sourceId, setSourceId] = useState("");
  const [sourceQuantity, setSourceQuantity] = useState(0);
  const [sourceUnit, setSourceUnit] = useState("");
  const [outputs, setOutputs] = useState<StockDocumentLineInput[]>([emptyLine(), emptyLine()]);
  const [byShares, setByShares] = useState(false);
  const [shares, setShares] = useState<number[]>([50, 50]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const byId = useMemo(() => new Map(ingredients.map(item => [item.id, item])), [ingredients]);
  const filled = outputs.filter(line => line.ingredientId && line.quantity > 0);
  const sharesTotal = shares.slice(0, outputs.length).reduce((sum, share) => sum + (share || 0), 0);

  const ready =
    Boolean(sourceId) && sourceQuantity > 0 && filled.length > 0 && (!byShares || Math.abs(sharesTotal - 100) < 0.01);

  function updateOutput(index: number, patch: Partial<StockDocumentLineInput>) {
    setOutputs(current =>
      current.map((line, position) => {
        if (position !== index) return line;
        const next = { ...line, ...patch };

        // Единица подставляется за позицией: у грамма и литра списки разные.
        if (patch.ingredientId) {
          const ingredient = byId.get(patch.ingredientId);
          next.unit = ingredient?.entryUnits[0] ?? "";
        }
        return next;
      })
    );
  }

  async function submit() {
    setBusy(true);

    const ok = await run(
      () =>
        api.once.post("/api/admin/stock/processings", {
          ingredientId: sourceId,
          quantity: sourceQuantity,
          unit: sourceUnit,
          comment: comment.trim() || undefined,
          outputs: outputs
            .map((line, index) => ({ line, share: shares[index] }))
            .filter(row => row.line.ingredientId && row.line.quantity > 0)
            .map(row => ({
              ingredientId: row.line.ingredientId,
              quantity: row.line.quantity,
              unit: row.line.unit,
              ...(byShares ? { costShare: row.share ?? 0 } : {})
            }))
        }),
      "Переработка проведена"
    );

    setBusy(false);
    if (ok) await onSaved();
  }

  const source = byId.get(sourceId);

  return (
    <Modal
      title="Переработка"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={() => void submit()}>
            Провести
          </button>
        </>
      }
    >
      <div className="field-row">
        <label className="field">
          <span>Что перерабатываем</span>
          <select
            value={sourceId}
            onChange={event => {
              setSourceId(event.target.value);
              setSourceUnit(byId.get(event.target.value)?.entryUnits[0] ?? "");
            }}
          >
            <option value="">Выберите позицию</option>
            {ingredients.map(item => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Количество</span>
          <input
            type="number"
            min={0}
            step="0.001"
            value={sourceQuantity || ""}
            onChange={event => setSourceQuantity(Number(event.target.value))}
          />
        </label>

        <label className="field">
          <span>Единица</span>
          <select value={sourceUnit} onChange={event => setSourceUnit(event.target.value)} disabled={!source}>
            {(source?.entryUnits ?? []).map(unit => (
              <option key={unit} value={unit}>
                {UNIT_TITLES[unit] ?? unit}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="inline-check">
        <input type="checkbox" checked={byShares} onChange={event => setByShares(event.target.checked)} />
        Задать доли себестоимости вручную
      </label>

      <p className="tiny muted">
        Без долей себестоимость делится по весу. Филе и кости получаются из одной курицы, но стоят
        разного — поровну по весу кости выйдут дороже, чем есть на самом деле.
      </p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Что получилось</th>
              <th className="num">Количество</th>
              <th>Единица</th>
              {byShares ? <th className="num">Доля, %</th> : null}
              <th />
            </tr>
          </thead>
          <tbody>
            {outputs.map((line, index) => {
              const ingredient = byId.get(line.ingredientId);

              return (
                <tr key={index}>
                  <td>
                    <select
                      value={line.ingredientId}
                      onChange={event => updateOutput(index, { ingredientId: event.target.value })}
                    >
                      <option value="">Выберите позицию</option>
                      {ingredients.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      min={0}
                      step="0.001"
                      value={line.quantity || ""}
                      onChange={event => updateOutput(index, { quantity: Number(event.target.value) })}
                    />
                  </td>
                  <td>
                    <select
                      value={line.unit}
                      onChange={event => updateOutput(index, { unit: event.target.value })}
                      disabled={!ingredient}
                    >
                      {(ingredient?.entryUnits ?? []).map(unit => (
                        <option key={unit} value={unit}>
                          {UNIT_TITLES[unit] ?? unit}
                        </option>
                      ))}
                    </select>
                  </td>
                  {byShares ? (
                    <td className="num">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={shares[index] ?? 0}
                        onChange={event =>
                          setShares(current =>
                            current.map((share, position) =>
                              position === index ? Number(event.target.value) : share
                            )
                          )
                        }
                      />
                    </td>
                  ) : null}
                  <td>
                    {outputs.length > 1 ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          setOutputs(current => current.filter((_, position) => position !== index));
                          setShares(current => current.filter((_, position) => position !== index));
                        }}
                      >
                        Убрать
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            setOutputs(current => [...current, emptyLine()]);
            setShares(current => [...current, 0]);
          }}
        >
          + Выход
        </button>

        {byShares ? (
          <strong className={Math.abs(sharesTotal - 100) < 0.01 ? "" : "text-crit"}>
            Доли: {sharesTotal.toFixed(2)} %
          </strong>
        ) : null}
      </div>

      <label className="field">
        <span>Комментарий</span>
        <input value={comment} onChange={event => setComment(event.target.value)} maxLength={500} />
      </label>

      <p className="tiny muted">
        Выходов может быть меньше входа: усушка, обрезки, кости в мусор. Себестоимость при этом
        распределяется целиком, и оставшееся дорожает — так и есть на самом деле.
      </p>
    </Modal>
  );
}
